import { Injectable } from '@angular/core';

/**
 * StaffAccessService provides helper methods for role-based access control
 * based on the staff member data stored in localStorage after login.
 */
@Injectable()
export class StaffAccessService {

    /**
     * Store staff data from login response into localStorage.
     * Call this after a successful login when the response includes staff info.
     */
    storeStaffData(staffId: number, roles: string[], restricted?: boolean): void {
        if (restricted) {
            localStorage.setItem('staff_restricted', '1');
        } else {
            localStorage.removeItem('staff_restricted');
        }
        if (staffId) {
            localStorage.setItem('staff_id', staffId.toString());
        }
        if (roles && roles.length > 0) {
            localStorage.setItem('staff_roles', JSON.stringify(roles));
        }
    }

    /**
     * Clear staff data from localStorage (e.g., on logout).
     */
    clearStaffData(): void {
        localStorage.removeItem('staff_id');
        localStorage.removeItem('staff_roles');
        localStorage.removeItem('staff_restricted');
    }

    /**
     * Start a View as session: an admin opens the panel as a sales rep. The
     * admin's own session is set aside in view_as_backup and put back by
     * endViewAs(). The API makes the session read-only and ends it after an hour.
     */
    startViewAs(data: any, staffName: string): void {
        var keys = ['accessToken', 'loginId', 'userData', 'staff_id', 'staff_roles', 'staff_restricted', 'userRoles'];
        var backup = {};
        for (var i = 0; i < keys.length; i++) {
            backup[keys[i]] = localStorage.getItem(keys[i]);
        }
        localStorage.setItem('view_as_backup', JSON.stringify(backup));
        localStorage.setItem('view_as_name', staffName);

        localStorage.setItem('accessToken', data.token);
        localStorage.setItem('loginId', data.user.user_id);
        localStorage.setItem('userData', JSON.stringify({ email: data.user.email, name: data.user.name, image: data.user.image_url }));
        this.storeStaffData(data.staff_id, data.staff_roles || [], true);
    }

    isViewAs(): boolean {
        return !!localStorage.getItem('view_as_backup');
    }

    viewAsName(): string {
        return localStorage.getItem('view_as_name') || '';
    }

    /** Put the admin's own session back. Returns false if there was nothing to restore. */
    endViewAs(): boolean {
        var raw = localStorage.getItem('view_as_backup');
        localStorage.removeItem('view_as_backup');
        localStorage.removeItem('view_as_name');
        if (!raw) {
            return false;
        }
        var backup = {};
        try {
            backup = JSON.parse(raw);
        } catch (e) {
            return false;
        }
        Object.keys(backup).forEach(function (key) {
            if (backup[key] === null || backup[key] === undefined) {
                localStorage.removeItem(key);
            } else {
                localStorage.setItem(key, backup[key]);
            }
        });
        return true;
    }

    /**
     * A staff login (sales rep). The server holds it to its assigned companies,
     * with admin screens only on Sales Team Demo accounts and View Hub on the
     * rest; the panel trims the menu and actions to match.
     */
    isStaffLogin(): boolean {
        return localStorage.getItem('staff_restricted') === '1';
    }

    /**
     * Get the current user's staff_id from localStorage.
     * Returns null if no staff link exists.
     */
    getStaffId(): number {
        var id = localStorage.getItem('staff_id');
        if (id) {
            return parseInt(id, 10);
        }
        return null;
    }

    /**
     * Get the current user's staff roles from localStorage.
     * Returns an empty array if no staff link exists.
     */
    getRoles(): string[] {
        var rolesStr = localStorage.getItem('staff_roles');
        if (rolesStr) {
            try {
                return JSON.parse(rolesStr);
            } catch (e) {
                return [];
            }
        }
        return [];
    }

    /**
     * Check if the current user is a Sales Person (has only sales_person role).
     */
    isSalesPerson(): boolean {
        var roles = this.getRoles();
        if (roles.length === 0) {
            return false;
        }
        // Sales person: has 'sales_person' role and does NOT have 'account_manager' role
        return roles.indexOf('sales_person') > -1 && roles.indexOf('account_manager') === -1;
    }

    /**
     * Check if the current user is an Account Manager.
     */
    isAccountManager(): boolean {
        var roles = this.getRoles();
        return roles.indexOf('account_manager') > -1;
    }

    /**
     * Check if the current user has a staff link at all.
     */
    hasStaffLink(): boolean {
        return this.getStaffId() !== null;
    }

    /**
     * Check if the current user has full access (no staff link, or is account manager).
     * Users without a staff link get standard admin behavior.
     */
    hasFullAccess(): boolean {
        if (!this.hasStaffLink()) {
            return true;
        }
        // Account managers have full access
        if (this.isAccountManager()) {
            return true;
        }
        return false;
    }
}
